import { CTL_ROUTES, ONBOARDING_ROUTES } from '../../../navigation/paths';
import type { OnboardingState } from '../../close-the-loop';
import { returnPathOf } from '../../passcode-auth';

export const INVITE_ENTRY = 'invite';

export interface WizardRouteState {
  readonly entry?: typeof INVITE_ENTRY;
  readonly from?: string;
  readonly next?: string;
}

export type WizardOrigin =
  | { readonly kind: 'firstRun'; readonly invited: boolean; readonly then: string | null }
  | { readonly kind: 'return'; readonly to: string };

type KnownOnboarding = OnboardingState | null | undefined;

const isInviteState = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && 'entry' in state && (state as { entry: unknown }).entry === INVITE_ENTRY;

const nextOf = (state: unknown): string | null =>
  typeof state === 'object' && state !== null && 'next' in state ? returnPathOf({ from: (state as { next: unknown }).next }) : null;

export const wizardOriginOf = (state: unknown): WizardOrigin => {
  if (isInviteState(state)) return { kind: 'firstRun', invited: true, then: nextOf(state) };
  const to = returnPathOf(state);
  return to === null ? { kind: 'firstRun', invited: false, then: nextOf(state) } : { kind: 'return', to };
};

export const finishDestinationOf = (origin: WizardOrigin): string =>
  origin.kind === 'return' ? origin.to : origin.then ?? CTL_ROUTES.issuesList;

export const isOnboardingPending = (onboarding: KnownOnboarding): boolean => onboarding?.status === 'PENDING';

const isOnboardingPath = (pathname: string): boolean =>
  pathname === ONBOARDING_ROUTES.wizard || pathname.startsWith(`${ONBOARDING_ROUTES.wizard}/`);

export type GateStatus = 'known' | 'waiting' | 'unknown';

export interface GateReading {
  readonly known: boolean;
  readonly failed: boolean;
  readonly waitedLong: boolean;
}

export const gateStatusOf = ({ known, failed, waitedLong }: GateReading): GateStatus => {
  if (known) return 'known';
  return failed || waitedLong ? 'unknown' : 'waiting';
};

export interface GateFacts {
  readonly status: GateStatus;
  readonly onboarding: KnownOnboarding;
}

export interface GatePlace {
  readonly pathname: string;
  readonly search: string;
}

export type GateDecision =
  | { readonly kind: 'checking' }
  | { readonly kind: 'redirect'; readonly to: string; readonly state: WizardRouteState }
  | { readonly kind: 'pass' };

const PASS: GateDecision = { kind: 'pass' };
const CHECKING: GateDecision = { kind: 'checking' };

export const gateDecisionOf = ({ status, onboarding }: GateFacts, { pathname, search }: GatePlace): GateDecision => {
  if (isOnboardingPath(pathname)) return PASS;
  if (status === 'waiting') return CHECKING;
  if (status === 'unknown' || !isOnboardingPending(onboarding)) return PASS;
  return { kind: 'redirect', to: ONBOARDING_ROUTES.wizard, state: { next: `${pathname}${search}` } };
};

const hasEnded = (onboarding: KnownOnboarding): boolean => onboarding !== null && onboarding !== undefined && !isOnboardingPending(onboarding);

export const welcomesFirst = (origin: WizardOrigin, onboarding: KnownOnboarding): boolean => {
  if (origin.kind !== 'firstRun' || hasEnded(onboarding)) return false;
  return origin.invited || onboarding?.entry === 'INVITE';
};
